import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Input } from './ui/input';
import {
  X, Search, Filter, Flame, TrendingDown, TrendingUp, UserPlus, Zap,
  MapPin, Thermometer, DollarSign, Hash, ChevronDown, ChevronUp, RotateCcw,
} from 'lucide-react';
import { authHeaders } from '../utils/api';
import { usePersistedState } from '../hooks/usePersistedState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FilterSection({ title, icon: Icon, children, defaultOpen = false }) {
  // Persist per-section open state — key on the section title so each
  // filter group (Party, Amount, Location, etc.) remembers its own
  // collapse state across navigations and reloads.
  const [open, setOpen] = usePersistedState(
    `ui.investigation.section.${title.replace(/\s+/g, '_').toLowerCase()}.open`,
    defaultOpen,
  );
  return (
    <div className="border-b border-border/50 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-sm font-medium hover:bg-muted/30 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-muted-foreground" />
          {title}
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

function MultiSelect({ items, selected, onToggle, searchable = true }) {
  const [search, setSearch] = useState('');
  const filtered = search
    ? items.filter(i => i.name.toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div className="space-y-1.5">
      {searchable && (
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="h-7 text-xs pl-7"
          />
        </div>
      )}
      <div className="max-h-36 overflow-y-auto space-y-0.5 pr-1">
        {filtered.map(item => (
          <label
            key={item.name}
            className={`flex items-center gap-2 px-2 py-1 rounded text-xs cursor-pointer transition-colors ${
              selected.includes(item.name) ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted/50'
            }`}
          >
            <input
              type="checkbox"
              checked={selected.includes(item.name)}
              onChange={() => onToggle(item.name)}
              className="h-3 w-3 rounded accent-primary"
            />
            <span className="flex-1 truncate">{item.name}</span>
            <span className="text-muted-foreground font-mono">{item.count.toLocaleString()}</span>
          </label>
        ))}
        {filtered.length === 0 && (
          <p className="text-xs text-muted-foreground py-2 text-center">No results</p>
        )}
      </div>
    </div>
  );
}

export default function InvestigationPanel({ datasetIds, onApply, onClose, activeFilters }) {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filter state
  const [occupations, setOccupations] = useState(activeFilters?.occupations || []);
  const [employers, setEmployers] = useState(activeFilters?.employers || []);
  const [committees, setCommittees] = useState(activeFilters?.committees || []);
  const [minAmount, setMinAmount] = useState(activeFilters?.minAmount ?? '');
  const [maxAmount, setMaxAmount] = useState(activeFilters?.maxAmount ?? '');
  const [minYear, setMinYear] = useState(activeFilters?.minYear ?? '');
  const [maxYear, setMaxYear] = useState(activeFilters?.maxYear ?? '');
  const [minYearsActive, setMinYearsActive] = useState(activeFilters?.minYearsActive ?? '');
  const [maxYearsActive, setMaxYearsActive] = useState(activeFilters?.maxYearsActive ?? '');
  const [patterns, setPatterns] = useState(activeFilters?.patterns || []);
  const [mode, setMode] = useState(activeFilters?.mode || 'pins');

  const fetchOptions = useCallback(async () => {
    if (!datasetIds) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/map/investigation-options?dataset_ids=${datasetIds}`, { headers: authHeaders() });
      const data = await res.json();
      setOptions(data);
    } catch (e) {
      console.error('Failed to fetch investigation options:', e);
    } finally {
      setLoading(false);
    }
  }, [datasetIds]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  const toggleItem = (list, setList, item) => {
    setList(prev => prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]);
  };

  const togglePattern = (p) => {
    setPatterns(prev => prev.includes(p) ? prev.filter(i => i !== p) : [...prev, p]);
  };

  const activeCount = [
    occupations.length > 0,
    employers.length > 0,
    committees.length > 0,
    minAmount !== '' || maxAmount !== '',
    minYear !== '' || maxYear !== '',
    minYearsActive !== '' || maxYearsActive !== '',
    patterns.length > 0,
  ].filter(Boolean).length;

  const handleApply = () => {
    onApply({
      occupations: occupations.length > 0 ? occupations : null,
      employers: employers.length > 0 ? employers : null,
      committees: committees.length > 0 ? committees : null,
      minAmount: minAmount !== '' ? parseFloat(minAmount) : null,
      maxAmount: maxAmount !== '' ? parseFloat(maxAmount) : null,
      minYear: minYear !== '' ? parseInt(minYear) : null,
      maxYear: maxYear !== '' ? parseInt(maxYear) : null,
      minYearsActive: minYearsActive !== '' ? parseInt(minYearsActive) : null,
      maxYearsActive: maxYearsActive !== '' ? parseInt(maxYearsActive) : null,
      patterns: patterns.length > 0 ? patterns : null,
      mode,
    });
  };

  const handleReset = () => {
    setOccupations([]);
    setEmployers([]);
    setCommittees([]);
    setMinAmount('');
    setMaxAmount('');
    setMinYear('');
    setMaxYear('');
    setMinYearsActive('');
    setMaxYearsActive('');
    setPatterns([]);
    setMode('pins');
  };

  const patternOptions = [
    { id: 'stopped', label: 'Stopped Donating', icon: TrendingDown, desc: 'Active before but not in latest year', color: 'text-red-600 bg-red-50 border-red-200' },
    { id: 'new_donor', label: 'New Donors', icon: UserPlus, desc: 'First appeared in most recent year', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
    { id: 'surge', label: 'Surge / Spikes', icon: TrendingUp, desc: 'Large donations relative to average', color: 'text-amber-600 bg-amber-50 border-amber-200' },
    { id: 'high_frequency', label: 'High Frequency', icon: Zap, desc: '10+ donations per year on average', color: 'text-violet-600 bg-violet-50 border-violet-200' },
  ];

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-end bg-black/40" onClick={onClose}>
      <div
        className="bg-background h-full w-full max-w-md shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="investigation-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-900 text-white">
          <div>
            <h2 className="font-heading font-semibold text-sm flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Investigation Refinement
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {activeCount > 0 ? `${activeCount} filter${activeCount > 1 ? 's' : ''} active` : 'No filters applied'}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/10 h-8 w-8">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
              Loading filter options...
            </div>
          ) : options ? (
            <>
              {/* Display Mode */}
              <div className="px-4 py-3 border-b">
                <p className="text-xs font-medium text-muted-foreground mb-2">Display Mode</p>
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { id: 'pins', label: 'Pins', icon: MapPin },
                    { id: 'heat_dollars', label: '$ Heat Map', icon: DollarSign },
                    { id: 'heat_count', label: '# Heat Map', icon: Hash },
                  ].map(m => (
                    <button
                      key={m.id}
                      onClick={() => setMode(m.id)}
                      className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-medium transition-all ${
                        mode === m.id
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                      }`}
                      data-testid={`mode-${m.id}`}
                    >
                      <m.icon className="w-3.5 h-3.5" />
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Donation Patterns */}
              <FilterSection title="Donation Patterns" icon={Flame} defaultOpen={true}>
                <div className="space-y-1.5">
                  {patternOptions.map(p => (
                    <button
                      key={p.id}
                      onClick={() => togglePattern(p.id)}
                      className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded border text-xs transition-all ${
                        patterns.includes(p.id) ? p.color + ' font-medium' : 'border-border/50 hover:bg-muted/30'
                      }`}
                      data-testid={`pattern-${p.id}`}
                    >
                      <p.icon className="w-4 h-4 shrink-0" />
                      <div className="text-left flex-1">
                        <div className="font-medium">{p.label}</div>
                        <div className="text-muted-foreground text-[10px] mt-0.5">{p.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </FilterSection>

              {/* Occupation Filter */}
              <FilterSection title={`Occupation${occupations.length > 0 ? ` (${occupations.length})` : ''}`} icon={Search} defaultOpen={false}>
                <MultiSelect
                  items={options.occupations}
                  selected={occupations}
                  onToggle={(item) => toggleItem(occupations, setOccupations, item)}
                />
              </FilterSection>

              {/* Employer Filter */}
              <FilterSection title={`Employer${employers.length > 0 ? ` (${employers.length})` : ''}`} icon={Search} defaultOpen={false}>
                <MultiSelect
                  items={options.employers}
                  selected={employers}
                  onToggle={(item) => toggleItem(employers, setEmployers, item)}
                />
              </FilterSection>

              {/* Committee Filter */}
              <FilterSection title={`Committee${committees.length > 0 ? ` (${committees.length})` : ''}`} icon={Search} defaultOpen={false}>
                <MultiSelect
                  items={options.committees}
                  selected={committees}
                  onToggle={(item) => toggleItem(committees, setCommittees, item)}
                />
              </FilterSection>

              {/* Dollar Amount Range */}
              <FilterSection title="Dollar Amount Range" icon={DollarSign} defaultOpen={false}>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground">Min ($)</label>
                    <Input
                      type="number"
                      value={minAmount}
                      onChange={(e) => setMinAmount(e.target.value)}
                      placeholder={String(options.amount_range[0])}
                      className="h-7 text-xs"
                      data-testid="min-amount-input"
                    />
                  </div>
                  <span className="text-muted-foreground mt-3">to</span>
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground">Max ($)</label>
                    <Input
                      type="number"
                      value={maxAmount}
                      onChange={(e) => setMaxAmount(e.target.value)}
                      placeholder={String(options.amount_range[1])}
                      className="h-7 text-xs"
                      data-testid="max-amount-input"
                    />
                  </div>
                </div>
                {/* Quick presets */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {[
                    { label: '$0-$200', min: 0, max: 200 },
                    { label: '$200-$2,800', min: 200, max: 2800 },
                    { label: '$2,800+', min: 2800, max: '' },
                    { label: '$10K+', min: 10000, max: '' },
                  ].map(p => (
                    <button
                      key={p.label}
                      onClick={() => { setMinAmount(String(p.min)); setMaxAmount(String(p.max)); }}
                      className="text-[10px] px-2 py-0.5 rounded border hover:bg-muted/50 transition-colors"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </FilterSection>

              {/* Date Range */}
              <FilterSection title="Date Range (Year)" icon={Search} defaultOpen={false}>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground">From</label>
                    <Input
                      type="number"
                      value={minYear}
                      onChange={(e) => setMinYear(e.target.value)}
                      placeholder={String(options.year_range[0])}
                      className="h-7 text-xs"
                      data-testid="min-year-input"
                    />
                  </div>
                  <span className="text-muted-foreground mt-3">to</span>
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground">To</label>
                    <Input
                      type="number"
                      value={maxYear}
                      onChange={(e) => setMaxYear(e.target.value)}
                      placeholder={String(options.year_range[1])}
                      className="h-7 text-xs"
                      data-testid="max-year-input"
                    />
                  </div>
                </div>
              </FilterSection>

              {/* Years Active */}
              <FilterSection title="Years Active" icon={Thermometer} defaultOpen={false}>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground">Min years</label>
                    <Input
                      type="number"
                      value={minYearsActive}
                      onChange={(e) => setMinYearsActive(e.target.value)}
                      placeholder="1"
                      min="1"
                      className="h-7 text-xs"
                      data-testid="min-years-input"
                    />
                  </div>
                  <span className="text-muted-foreground mt-3">to</span>
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground">Max years</label>
                    <Input
                      type="number"
                      value={maxYearsActive}
                      onChange={(e) => setMaxYearsActive(e.target.value)}
                      placeholder="Any"
                      className="h-7 text-xs"
                      data-testid="max-years-input"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {[
                    { label: '1 year only', min: 1, max: 1 },
                    { label: '2-3 years', min: 2, max: 3 },
                    { label: '4+ years', min: 4, max: '' },
                  ].map(p => (
                    <button
                      key={p.label}
                      onClick={() => { setMinYearsActive(String(p.min)); setMaxYearsActive(String(p.max)); }}
                      className="text-[10px] px-2 py-0.5 rounded border hover:bg-muted/50 transition-colors"
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </FilterSection>
            </>
          ) : (
            <div className="py-16 text-center text-sm text-muted-foreground">
              Failed to load options
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-4 py-3 flex items-center gap-2 bg-muted/30">
          <Button variant="outline" size="sm" onClick={handleReset} className="text-xs" data-testid="reset-investigation">
            <RotateCcw className="w-3 h-3 mr-1.5" />
            Reset
          </Button>
          <Button size="sm" onClick={handleApply} className="flex-1 text-xs" data-testid="apply-investigation">
            <Filter className="w-3 h-3 mr-1.5" />
            Apply Investigation Filters
          </Button>
        </div>
      </div>
    </div>
  );
}
